import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { Button, TextField, LinearProgress } from "@material-ui/core";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import "./PaginationToolbar.css";

@inject("agentActStore", "profileStore")
@observer
class PaginationToolbar extends Component {
    state = {
        pageInputValue: "1"
    };

    componentDidMount() {
        this.setState({ pageInputValue: String(this.props.agentActStore.paginationState.currentPage) });
    }

    componentDidUpdate(prevProps) {
        const { currentPage } = this.props.agentActStore.paginationState;
        if (this.lastCurrentPage !== currentPage) {
            this.lastCurrentPage = currentPage;
            this.setState({ pageInputValue: String(currentPage) });
        }
    }

    handlePreviousPage = () => {
        const { paginationState } = this.props.agentActStore;
        if (paginationState.currentPage > 1) {
            this.loadPage(paginationState.currentPage - 1);
        }
    };

    handleNextPage = () => {
        const { paginationState, isMoreAgentAct } = this.props.agentActStore;
        const totalPages = this.getTotalPages();
        if (paginationState.currentPage < totalPages || isMoreAgentAct) {
            this.loadPage(paginationState.currentPage + 1);
        }
    };

    handlePageInputChange = (e) => {
        this.setState({ pageInputValue: e.target.value });
    };

    handlePageInputKeyDown = (e) => {
        if (e.key === "Enter") {
            this.handlePageInputBlur();
        }
    };

    handlePageInputBlur = () => {
        const { pageInputValue } = this.state;
        const pageNum = parseInt(pageInputValue, 10);
        const totalPages = this.getTotalPages();
        
        if (!isNaN(pageNum) && pageNum >= 1) {
            const targetPage = Math.min(pageNum, totalPages || pageNum);
            this.loadPage(targetPage);
        } else {
            this.setState({ pageInputValue: String(this.props.agentActStore.paginationState.currentPage) });
        }
    };

    loadPage = (pageNum) => {
        const { agentActStore, profileStore } = this.props;
        const currentProfile = profileStore.getCurrentProfile();
        agentActStore.loadSpecificPage(currentProfile?.id, pageNum);
    };

    handleLoadAll = () => {
        const { agentActStore, profileStore } = this.props;
        const currentProfile = profileStore.getCurrentProfile();
        agentActStore.loadAllActs(currentProfile?.id);
    };

    handleCancelLoadAll = () => {
        this.props.agentActStore.cancelLoadAll();
    };

    handleResetToPaginated = () => {
        const { agentActStore, profileStore } = this.props;
        const currentProfile = profileStore.getCurrentProfile();
        agentActStore.resetPaginationState();
        agentActStore.resetAgentActs();
        agentActStore.loadSpecificPage(currentProfile?.id, 1);
    };

    getTotalPages = () => {
        const { paginationState, isMoreAgentAct } = this.props.agentActStore;
        const { totalLoaded, cachedPages } = paginationState;
        const terminalPages = Object.entries(this.props.agentActStore.rosterPageMore || {})
            .filter(([, isMore]) => isMore === false)
            .map(([page]) => Number(page));
        if (terminalPages.length) return Math.min(...terminalPages);
        
        const maxCachedPage = Object.keys(cachedPages).length > 0 
            ? Math.max(...Object.keys(cachedPages).map(Number)) 
            : 1;
        
        if (!isMoreAgentAct && totalLoaded > 0) {
            return Math.max(paginationState.currentPage, maxCachedPage);
        }
        
        return isMoreAgentAct ? maxCachedPage + 1 : maxCachedPage;
    };

    isAllLoaded = () => {
        return this.props.agentActStore.paginationState.allLoaded;
    };

    render() {
        const { agentActStore, totalFilteredCount } = this.props;
        const { paginationState, isMoreAgentAct, agentActs, isLoadingAcceptedActs } = agentActStore;
        const { currentPage, pageSize, isLoadingAll, loadAllProgress, totalLoaded } = paginationState;
        const { pageInputValue } = this.state;

        const allLoaded = this.isAllLoaded();
        const totalPages = allLoaded ? 1 : this.getTotalPages();
        
        const startItem = allLoaded ? 1 : (currentPage - 1) * pageSize + 1;
        const endItem = allLoaded ? agentActs.length : Math.min(startItem + pageSize - 1, startItem + agentActs.length - 1);
        const displayTotal = allLoaded ? agentActs.length : (!isMoreAgentAct ? totalLoaded || agentActs.length : `${totalLoaded || agentActs.length}+`);

        const hasPrevious = !allLoaded && currentPage > 1;
        const hasNext = !allLoaded && (currentPage < totalPages || isMoreAgentAct);

        if (isLoadingAll) {
            return (
                <div className="pagination-toolbar">
                    <div className="pagination-loading-all">
                        <div className="loading-progress-container">
                            <LinearProgress 
                                variant="determinate" 
                                value={loadAllProgress} 
                                className="loading-progress-bar"
                            />
                        </div>
                        <span className="loading-progress-text">
                            Loading... <strong>{totalLoaded}</strong> acts
                        </span>
                        <Button 
                            size="small" 
                            onClick={this.handleCancelLoadAll}
                            className="cancel-load-btn"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            );
        }

        if (allLoaded) {
            return (
                <div className="pagination-toolbar pagination-toolbar-all-loaded">
                    <div className="all-loaded-info">
                        <CheckCircleIcon className="all-loaded-icon" />
                        <span>Showing all <strong>{agentActs.length}</strong> acts (sorted A-Z)</span>
                    </div>
                    <button 
                        className="reset-view-link"
                        onClick={this.handleResetToPaginated}
                    >
                        Reset to paginated view
                    </button>
                </div>
            );
        }

        return (
            <div className="pagination-toolbar">
                <div className="pagination-info">
                    Showing <strong>{startItem}-{endItem}</strong> of <strong>{displayTotal}</strong> acts
                    {totalFilteredCount !== undefined && totalFilteredCount !== agentActs.length && (
                        <span className="filtered-notice"> ({totalFilteredCount} filtered)</span>
                    )}
                </div>

                <div className="pagination-controls">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={this.handlePreviousPage}
                        disabled={!hasPrevious || isLoadingAcceptedActs}
                        className="pagination-nav-btn"
                    >
                        <NavigateBeforeIcon fontSize="small" />
                        Prev
                    </Button>

                    <div className="page-selector">
                        <span>Page</span>
                        <TextField
                            value={pageInputValue}
                            onChange={this.handlePageInputChange}
                            onBlur={this.handlePageInputBlur}
                            onKeyDown={this.handlePageInputKeyDown}
                            variant="outlined"
                            size="small"
                            className="page-input"
                            inputProps={{ style: { textAlign: "center", padding: "6px 8px" } }}
                        />
                        <span className="total-pages">of {isMoreAgentAct ? `${totalPages}+` : totalPages}</span>
                    </div>

                    <Button
                        variant="contained"
                        size="small"
                        onClick={this.handleNextPage}
                        disabled={!hasNext || isLoadingAcceptedActs}
                        className="pagination-nav-btn pagination-nav-btn-primary"
                    >
                        Next
                        <NavigateNextIcon fontSize="small" />
                    </Button>
                </div>

                {isMoreAgentAct && (
                    <div className="load-all-section">
                        <div className="divider" />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={this.handleLoadAll}
                            disabled={isLoadingAcceptedActs}
                            className="load-all-btn"
                        >
                            <GetAppIcon fontSize="small" style={{ marginRight: 4 }} />
                            Load All
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}

export default PaginationToolbar;
